/**
 * Autelpilot click + navigation tracking (Navigation V2).
 * Deploy to Shopify Theme assets — DO NOT publish to production until preview verified.
 */
(function () {
  "use strict";

  var TRACKING_VERSION = "navigation_v2";
  var ENABLE_NAVIGATION_EXPOSURE_EVENTS = true;
  var NAV_HOVER_MS = 400;
  var VISITOR_STORAGE_KEY = "autelpilot_visitor_id";
  var SESSION_STORAGE_KEY = "autelpilot_session_id";
  var IMPRESSION_DEDUP_STORAGE_KEY = "autelpilot_nav_impression_keys";
  var NAV_IMPRESSION_THRESHOLD = 0.5;

  // ── Original helpers (preserved) ────────────────────────────────────────

  function getElementClassName(el) {
    if (!el || !el.className) return "";
    return typeof el.className === "string"
      ? el.className
      : el.className.baseVal || "";
  }

  function formatDomTreeSegment(el) {
    if (!el || el.nodeType !== 1) return "";
    var tag = el.tagName.toLowerCase();
    var id = el.id;
    var classes = getElementClassName(el).trim().split(/\s+/).filter(Boolean);
    if (id === "MainContent") {
      return tag + (classes.length ? "." + classes.join(".") : "") + "#" + id;
    }
    var segment = tag;
    if (classes.length) segment += "." + classes.join(".");
    if (id) segment += "#" + id;
    return segment;
  }

  function getDomTreePath(el, options) {
    options = options || {};
    var stopSelector = options.stopSelector || "html";
    var maxDepth = options.maxDepth || 30;
    var segments = [];
    var current = el;
    var depth = 0;
    while (
      current &&
      current.nodeType === 1 &&
      depth < maxDepth &&
      (segments.unshift(formatDomTreeSegment(current)),
      !(current.matches && current.matches(stopSelector)) &&
      current !== document.documentElement)
    ) {
      current = current.parentElement;
      depth++;
    }
    return segments.join(" > ");
  }

  function getClickText(rawTarget, clickEl) {
    var accessoryItem =
      (rawTarget && rawTarget.closest && rawTarget.closest(".accessory-item")) ||
      (clickEl && clickEl.closest && clickEl.closest(".accessory-item"));
    if (accessoryItem) {
      var accessoryTitle = accessoryItem.querySelector(".accessory-item-title");
      if (accessoryTitle && accessoryTitle.textContent) {
        var titleText = accessoryTitle.textContent.trim();
        if (titleText) return titleText.slice(0, 100);
      }
    }
    var sources = [rawTarget, clickEl];
    for (var i = 0; i < sources.length; i++) {
      var el = sources[i];
      if (!el || el.nodeType !== 1) continue;
      var alt = el.getAttribute && el.getAttribute("alt");
      if (alt && alt.trim()) return alt.trim().slice(0, 100);
      var ariaLabel = el.getAttribute && el.getAttribute("aria-label");
      if (ariaLabel && ariaLabel.trim()) return ariaLabel.trim().slice(0, 100);
      var lines = (el.textContent || "")
        .trim()
        .split(/\s*\n+\s*/)
        .filter(Boolean);
      if (lines.length) {
        return lines.length === 1 || lines[0].length <= 80
          ? lines[0].slice(0, 100)
          : lines.join(" ").slice(0, 100);
      }
    }
    return "";
  }

  function resolveClickElement(rawTarget) {
    var el = rawTarget.nodeType === 1 ? rawTarget : rawTarget.parentElement;
    if (!el) return null;
    if (el.closest("svg, path, use")) {
      el = el.closest("svg, path, use").parentElement || el;
    }
    return (
      el.closest(
        'a, button, img, input, label, [role="tab"], [role="button"], .product-tabs-header-item, .product-media-tabs__btn, .product-form__submit, .pv-quick-link, .product-sale-banner, .discount-box, .back-to-top, [data-back-to-top], .header__menu-item, .header__icon, .accessory-item, li, h1, h2, h3, h4, p',
      ) || el.closest(".product-tabs-body-item")
    );
  }

  function isInteractiveClickElement(el) {
    if (!el || !el.matches) return false;
    return el.matches(
      'a, button, img, input, label, [role="tab"], [role="button"], .product-tabs-header-item, .product-media-tabs__btn, .product-form__submit, .pv-quick-link, .product-sale-banner, .discount-box, .back-to-top, [data-back-to-top], .header__menu-item, .header__icon, .accessory-item',
    );
  }

  function isLayoutShellElement(el) {
    if (!el || !el.matches) return false;
    return el.matches(
      "product-info, section, main, .page-width, .product, .grid, .grid__item, .product__info-wrapper, .product__info-container, .product__media-wrapper, .header-wrapper, .content-for-layout, .product-tabs, .product-tabs-body",
    );
  }

  function shouldTrackClick(rawTarget, clickEl, clickText) {
    if (!clickEl) return false;
    if (
      isInteractiveClickElement(clickEl) ||
      (clickEl.matches("h1, h2, h3, h4, p, li") && clickText) ||
      (clickEl.closest(".product-tabs-body-item") && clickText)
    ) {
      return true;
    }
    if (isLayoutShellElement(rawTarget) || isLayoutShellElement(clickEl)) {
      return false;
    }
    return !!clickText;
  }

  function getClickModule(el) {
    if (el.closest(".back-to-top, [data-back-to-top]")) return "back-to-top";
    if (el.closest('[class*="as-wrapper"], [class*="as-slide"], .announcement-bar')) {
      return "announcement-bar";
    }
    if (
      el.closest(
        ".header-wrapper, sticky-header, .shopify-section-group-header-group, header",
      )
    ) {
      return "header";
    }
    if (el.closest(".product-sale-banner, .discount-box")) {
      return "product-sale-discount";
    }
    if (el.closest(".product-tabs")) return "product-tabs";
    if (el.closest(".product-media-tabs")) return "product-media-tabs";
    if (el.closest(".accessory-binding, .accessory-item")) {
      return "product-accessory";
    }
    if (el.closest(".product-form-buyBtns, .product-form, .buyMe")) {
      return "product-buy";
    }
    if (el.closest(".product-version")) return "product-version";
    if (el.closest(".product__info-container")) return "product-info";
    if (el.closest("product-info")) return "product-main";
    return "other";
  }

  function isHeaderAnnouncementClickTarget(el) {
    return el.closest(
      '.shopify-section-group-header-group, [class*="as-wrapper"], [class*="as-slide"], .announcement-bar',
    );
  }

  function isHeaderNavigationClickTarget(el) {
    if (!el || !el.closest) return false;
    return !!(
      el.closest("header-menu") ||
      el.closest(".mega-menu") ||
      el.closest("nav.header__inline-menu") ||
      el.closest("#menu-drawer") ||
      el.closest(".menu-drawer") ||
      el.closest("a.header__nav-promo") ||
      el.closest("#HeaderMenu-contact-us")
    );
  }

  function isTrackableClickTarget(el) {
    if (el.closest(".skip-to-content-link.visually-hidden")) return null;
    if (isHeaderAnnouncementClickTarget(el)) return true;
    if (isHeaderNavigationClickTarget(el)) return true;
    if (window.__clickTrackProductBody) {
      return el.closest("#MainContent, .back-to-top, [data-back-to-top]");
    }
    return null;
  }

  // ── Endpoints & config ────────────────────────────────────────────────────

  var PRODUCT_CLICK_SUPABASE_ENDPOINT =
    window.__productClickSupabaseEndpoint || "";
  var PRODUCT_CLICK_SHEET_ENDPOINT = window.__productClickSheetEndpoint || "";
  var PRODUCT_CLICK_BYPASS_COUNTRY = !!window.__productClickBypassCountry;
  var NAV_TRACKING_DEBUG = !!window.__NAV_TRACKING_DEBUG__;
  var PRODUCT_CLICK_SHEET_COUNTRIES = ["US", "CA"];
  var PRODUCT_CLICK_GA4_ENABLED = false;
  var PRODUCT_CLICK_MARKET_COUNTRY = (
    window.__productClickMarketCountryCode || ""
  ).toUpperCase();
  var PRODUCT_CLICK_IP_COUNTRY = "";
  var PRODUCT_CLICK_COUNTRY_READY = false;
  var PRODUCT_CLICK_COUNTRY_INIT_STARTED = false;
  var PRODUCT_CLICK_PENDING_SHEETS = [];
  var PRODUCT_CLICK_TRACKING_ENABLED = !!(
    PRODUCT_CLICK_SUPABASE_ENDPOINT || PRODUCT_CLICK_SHEET_ENDPOINT
  );
  var PRODUCT_CLICK_SHOP_NAME = (window.__productClickShopName || "").trim();
  var PRODUCT_CLICK_MENU_OPEN_TIME = 0;

  // ── Identity (Navigation V1) ──────────────────────────────────────────────

  function createUuid() {
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
      return crypto.randomUUID();
    }
    if (typeof crypto !== "undefined" && crypto.getRandomValues) {
      var bytes = new Uint8Array(16);
      crypto.getRandomValues(bytes);
      bytes[6] = (bytes[6] & 0x0f) | 0x40;
      bytes[8] = (bytes[8] & 0x3f) | 0x80;
      var hex = Array.from(bytes, function (b) {
        return b.toString(16).padStart(2, "0");
      }).join("");
      return (
        hex.slice(0, 8) +
        "-" +
        hex.slice(8, 12) +
        "-" +
        hex.slice(12, 16) +
        "-" +
        hex.slice(16, 20) +
        "-" +
        hex.slice(20)
      );
    }
    return "v-" + Date.now() + "-" + Math.random().toString(36).slice(2, 11);
  }

  function getVisitorId() {
    try {
      var id = localStorage.getItem(VISITOR_STORAGE_KEY);
      if (!id) {
        id = createUuid();
        localStorage.setItem(VISITOR_STORAGE_KEY, id);
      }
      return id;
    } catch (err) {
      return createUuid();
    }
  }

  function getSessionId() {
    try {
      var id = sessionStorage.getItem(SESSION_STORAGE_KEY);
      if (!id) {
        id = createUuid();
        sessionStorage.setItem(SESSION_STORAGE_KEY, id);
      }
      return id;
    } catch (err) {
      return createUuid();
    }
  }

  // ── Navigation context (Navigation V1) ───────────────────────────────────

  function normalizePathname(href) {
    if (!href) return null;
    var trimmed = String(href).trim();
    if (!trimmed || trimmed === "#") return null;
    try {
      var parsed = new URL(trimmed, window.location.origin);
      return parsed.pathname || null;
    } catch (err) {
      if (trimmed.charAt(0) === "/") {
        return trimmed.split("?")[0].split("#")[0];
      }
      return null;
    }
  }

  function getSessionNavRoot() {
    try {
      return sessionStorage.getItem("headerNavRoot");
    } catch (err) {
      return null;
    }
  }

  function resolveClickTargetUrl(rawTarget, clickEl) {
    var rawEl =
      rawTarget && rawTarget.nodeType === 1
        ? rawTarget
        : rawTarget && rawTarget.parentElement;
    var anchor = rawEl && rawEl.closest && rawEl.closest("a[href]");
    if (!anchor && clickEl && clickEl.closest) {
      anchor = clickEl.closest("a[href]");
    }
    return anchor ? normalizePathname(anchor.href) : null;
  }

  function buildMobilePanelId(navigationRoot, drawerSlug) {
    if (!navigationRoot || !drawerSlug) return null;
    var rootPrefix = navigationRoot + "-";
    if (drawerSlug.indexOf(rootPrefix) === 0) {
      return "panel-" + drawerSlug;
    }
    return "panel-" + navigationRoot + "-" + drawerSlug;
  }

  function getNavigationContext(contextEl, rawTarget) {
    if (!contextEl) {
      return {
        navigation_root: getSessionNavRoot(),
        panel_id: null,
        click_target_url: resolveClickTargetUrl(rawTarget, null),
      };
    }

    var headerMenu =
      contextEl.closest &&
      contextEl.closest("header-menu[data-header-nav-root]");
    var panelWithId =
      contextEl.closest && contextEl.closest(".mega-menu__panel[id]");
    var l2 =
      contextEl.closest &&
      contextEl.closest("li.mega-menu__level2-item[data-target]");
    var drawerDetails =
      contextEl.closest &&
      contextEl.closest('details[id^="Details-menu-drawer-"]');
    if (drawerDetails && /drawer-container/i.test(drawerDetails.id || "")) {
      drawerDetails = null;
    }
    var section =
      contextEl.closest &&
      contextEl.closest('details[id^="Details-menu-section-"]');

    var navigation_root =
      headerMenu && headerMenu.dataset && headerMenu.dataset.headerNavRoot
        ? headerMenu.dataset.headerNavRoot
        : null;

    if (!navigation_root && section && section.id) {
      navigation_root = section.id.replace(/^Details-menu-section-/, "");
    }

    if (!navigation_root) {
      navigation_root = getSessionNavRoot();
    }

    var panel_id =
      (panelWithId && panelWithId.id) ||
      (l2 && l2.dataset && l2.dataset.target) ||
      null;

    if (!panel_id && drawerDetails && drawerDetails.id && navigation_root) {
      var drawerSlug = drawerDetails.id.replace(/^Details-menu-drawer-/, "");
      if (drawerSlug && drawerSlug !== "container") {
        panel_id = buildMobilePanelId(navigation_root, drawerSlug);
      }
    }

    var click_target_url = resolveClickTargetUrl(rawTarget, contextEl);

    return {
      navigation_root: navigation_root,
      panel_id: panel_id,
      click_target_url: click_target_url,
    };
  }

  function isHeaderNavigationElement(clickEl) {
    if (!clickEl || !clickEl.closest) return false;
    return !!(
      clickEl.closest("header-menu") ||
      clickEl.closest(".mega-menu") ||
      clickEl.closest("#menu-drawer") ||
      clickEl.closest(".header__inline-menu") ||
      clickEl.closest("a.header__nav-promo") ||
      clickEl.closest("#HeaderMenu-contact-us")
    );
  }

  function inferMobileNavigationEventType(clickEl) {
    if (!clickEl || !clickEl.closest) return "click";
    var drawer = document.getElementById("menu-drawer");
    if (!drawer || !drawer.contains(clickEl)) return "click";

    // Outermost hamburger opens drawer — legacy click only, not funnel menu_open.
    if (clickEl.closest("summary.header__icon--menu")) {
      return "click";
    }
    // L1 business section (Autel Drones, Shop by Category, etc.)
    if (
      clickEl.closest(".menu-drawer__section-summary") &&
      clickEl.matches &&
      clickEl.matches("summary")
    ) {
      return "navigation_menu_open";
    }
    if (
      clickEl.closest("summary.menu-drawer__menu-item--level2") ||
      clickEl.closest("summary#HeaderDrawer-")
    ) {
      return "navigation_panel_view";
    }
    if (
      clickEl.closest("a.menu-drawer__product-item") ||
      (clickEl.closest(".menu-drawer__menu--level3") &&
        clickEl.closest("a[href]"))
    ) {
      return "navigation_click";
    }
    if (clickEl.closest("a[href]")) {
      return "navigation_click";
    }
    return "click";
  }

  function resolveNavigationClickEventType(clickEl, clickModule, clickTargetUrl) {
    if (clickModule !== "header" || !isHeaderNavigationElement(clickEl)) {
      return "click";
    }
    if (clickTargetUrl) {
      return "navigation_click";
    }
    // V2: exposure events (menu_open, panel_view, drawer_open, back) use dedicated listeners.
    // Mobile L1/L2 and Desktop non-URL header clicks stay event_type=click.
    return "click";
  }

  function isMobileNavigationBackButton(el) {
    if (!el || !el.closest) return null;
    return el.closest(
      "button.menu-drawer__close-button, button.menu-drawer__close-button--rich",
    );
  }

  function resolveMobileBackLevels(backEl) {
    var navCtx = getNavigationContext(backEl, backEl);
    var l2Details =
      backEl.closest && backEl.closest('details[id^="Details-menu-drawer-"]');
    if (l2Details && /drawer-container/i.test(l2Details.id || "")) {
      l2Details = null;
    }
    var l1Section =
      backEl.closest && backEl.closest('details[id^="Details-menu-section-"]');

    if (l2Details && l2Details.open) {
      return {
        from_level: 3,
        to_level: 2,
        navigation_root: navCtx.navigation_root,
        panel_id: navCtx.panel_id,
      };
    }
    if (l1Section && l1Section.open && !l2Details) {
      return {
        from_level: 2,
        to_level: 1,
        navigation_root: navCtx.navigation_root,
        panel_id: null,
      };
    }
    return {
      from_level: 2,
      to_level: 1,
      navigation_root: navCtx.navigation_root,
      panel_id: navCtx.panel_id,
    };
  }

  function sendMobileNavigationBack(backEl, rawTarget) {
    var levels = resolveMobileBackLevels(backEl);
    sendNavigationEvent({
      event_type: "navigation_back",
      navigation_root: levels.navigation_root,
      panel_id: levels.panel_id,
      from_level: levels.from_level,
      to_level: levels.to_level,
      click_element_text: getClickText(rawTarget, backEl),
      click_element_selector: "nav-track:navigation_back",
      click_element_dom_tree: buildSyntheticNavDomTree(
        "navigation_back",
        levels.navigation_root,
        levels.panel_id,
      ),
      click_raw_target: "navigation",
    });
  }

  function initMobileDrawerOpenTracking() {
    var detailsEl = document.querySelector(
      "details#Details-menu-drawer-container, details.menu-drawer-container",
    );
    if (!detailsEl || detailsEl.dataset.navDrawerOpenBound === "1") return;
    detailsEl.dataset.navDrawerOpenBound = "1";

    detailsEl.addEventListener("toggle", function () {
      if (getDeviceType() !== "mobile") return;
      if (!detailsEl.open) return;
      sendNavigationEvent({
        event_type: "navigation_drawer_open",
        navigation_root: null,
        panel_id: null,
        click_element_text: "Menu Drawer Open",
        click_element_selector: "nav-track:navigation_drawer_open",
        click_element_dom_tree: buildSyntheticNavDomTree(
          "navigation_drawer_open",
          null,
          null,
        ),
        click_raw_target: "navigation",
      });
    });
  }

  // ── Menu duration (mobile, preserved) ─────────────────────────────────────

  function initMenuDurationTracking() {
    var detailsEl = document.querySelector("details.menu-drawer-container");
    if (!detailsEl || typeof MutationObserver === "undefined") return;
    var observer = new MutationObserver(function () {
      if (detailsEl.classList.contains("menu-opening")) {
        PRODUCT_CLICK_MENU_OPEN_TIME = Date.now();
      } else {
        PRODUCT_CLICK_MENU_OPEN_TIME = 0;
      }
    });
    observer.observe(detailsEl, {
      attributes: true,
      attributeFilter: ["class"],
    });
  }

  function getMenuDurationMs() {
    return PRODUCT_CLICK_MENU_OPEN_TIME
      ? Date.now() - PRODUCT_CLICK_MENU_OPEN_TIME
      : null;
  }

  function getHeaderMenuContext(clickEl, rawTarget) {
    var viewAllDesktop =
      (rawTarget &&
        rawTarget.closest &&
        rawTarget.closest(".mega-menu__product-item--view-all")) ||
      (clickEl &&
        clickEl.closest &&
        clickEl.closest(".mega-menu__product-item--view-all"));
    if (viewAllDesktop) {
      return { menu_level: 3, is_view_all: true };
    }

    var drawer = document.getElementById("menu-drawer");
    var inMenuDrawer = drawer && drawer.contains(clickEl);

    if (!inMenuDrawer) {
      if (clickEl.closest && clickEl.closest(".mega-menu__product-item")) {
        return { menu_level: 3, is_view_all: false };
      }
      return null;
    }

    var viewAllMobile =
      (clickEl.classList &&
        clickEl.classList.contains("menu-drawer__product-item--view-all")) ||
      (rawTarget &&
        rawTarget.closest &&
        rawTarget.closest(".menu-drawer__product-item--view-all"));
    if (viewAllMobile) {
      return { menu_level: 3, is_view_all: true };
    }
    if (clickEl.closest(".menu-drawer__menu--level3")) {
      return { menu_level: 3, is_view_all: false };
    }
    if (clickEl.closest(".menu-drawer__menu--section-children")) {
      return { menu_level: 2, is_view_all: false };
    }
    if (clickEl.closest(".menu-drawer__menu--level1")) {
      return { menu_level: 1, is_view_all: false };
    }
    return { menu_level: 0, is_view_all: false };
  }

  function getDeviceType() {
    return typeof window.matchMedia === "function" &&
      window.matchMedia("(min-width: 990px)").matches
      ? "desktop"
      : "mobile";
  }

  function getClickPageTitle() {
    var title = (document.title || "").trim();
    if (!title || !PRODUCT_CLICK_SHOP_NAME) return title;
    var escapedShopName = PRODUCT_CLICK_SHOP_NAME.replace(
      /[.*+?^${}()|[\]\\]/g,
      "\\$&",
    );
    var suffixPattern = new RegExp(
      "\\s*[\\u2013\\-|]\\s*" + escapedShopName + "\\s*$",
    );
    var cleaned = title.replace(suffixPattern, "").trim();
    return cleaned || title;
  }

  // ── Send pipeline (preserved + extended) ─────────────────────────────────

  function getProductClickCountryCode() {
    return PRODUCT_CLICK_IP_COUNTRY || PRODUCT_CLICK_MARKET_COUNTRY;
  }

  function isProductClickTargetMarket() {
    return (
      PRODUCT_CLICK_SHEET_COUNTRIES.indexOf(PRODUCT_CLICK_MARKET_COUNTRY) !== -1
    );
  }

  function markProductClickCountryReady() {
    PRODUCT_CLICK_COUNTRY_READY = true;
    flushPendingSheetPayloads();
  }

  function initProductClickDetectedCountry() {
    if (!PRODUCT_CLICK_TRACKING_ENABLED || PRODUCT_CLICK_COUNTRY_INIT_STARTED) {
      return;
    }
    if (PRODUCT_CLICK_BYPASS_COUNTRY) {
      markProductClickCountryReady();
      return;
    }
    PRODUCT_CLICK_COUNTRY_INIT_STARTED = true;
    if (!isProductClickTargetMarket()) {
      markProductClickCountryReady();
      return;
    }
    var root =
      (window.Shopify &&
        window.Shopify.routes &&
        window.Shopify.routes.root) ||
      "/";
    var shopifyCountry = window.Shopify && window.Shopify.country;
    var url = root + "browsing_context_suggestions.json?country[enabled]=true";
    if (shopifyCountry) {
      url += "&country[exclude]=" + encodeURIComponent(shopifyCountry);
    }
    fetch(url)
      .then(function (res) {
        if (!res.ok) throw new Error("country detection failed");
        return res.json();
      })
      .then(function (data) {
        var detected =
          data && data.detected_values && data.detected_values.country;
        PRODUCT_CLICK_IP_COUNTRY = (
          (detected && detected.handle) ||
          ""
        ).toUpperCase();
      })
      .catch(function () {})
      .finally(markProductClickCountryReady);
  }

  function shouldSendClickToGoogleSheet() {
    if (PRODUCT_CLICK_BYPASS_COUNTRY) return true;
    if (!PRODUCT_CLICK_COUNTRY_READY) return false;
    var country = getProductClickCountryCode();
    if (!country) return false;
    return PRODUCT_CLICK_SHEET_COUNTRIES.indexOf(country) !== -1;
  }

  function postSupabasePayload(data) {
    try {
      fetch(PRODUCT_CLICK_SUPABASE_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
        keepalive: true,
      });
    } catch (err) {}
  }

  function postSheetPayload(sheetData) {
    try {
      fetch(PRODUCT_CLICK_SHEET_ENDPOINT, {
        method: "POST",
        mode: "no-cors",
        headers: { "Content-Type": "text/plain;charset=utf-8" },
        body: JSON.stringify(sheetData),
        keepalive: true,
      });
    } catch (err) {}
  }

  function markPdpViewAfterSuccessfulDelivery(payload) {
    if (
      payload &&
      payload.event_type === ECOMMERCE_EVENT_TYPES.productPageView &&
      payload.product_handle
    ) {
      markPdpViewForHandle(payload.product_handle);
    }
  }

  function deliverSheetPayload(payload) {
    if (!shouldSendClickToGoogleSheet()) return false;
    if (PRODUCT_CLICK_SUPABASE_ENDPOINT) {
      postSupabasePayload(payload);
      return true;
    }
    if (PRODUCT_CLICK_SHEET_ENDPOINT) {
      postSheetPayload(payload);
      return true;
    }
    return false;
  }

  function flushPendingSheetPayloads() {
    if (!PRODUCT_CLICK_PENDING_SHEETS.length) return;
    var queue = PRODUCT_CLICK_PENDING_SHEETS.slice();
    PRODUCT_CLICK_PENDING_SHEETS = [];
    queue.forEach(function (payload) {
      if (!deliverSheetPayload(payload)) return;
      markPdpViewAfterSuccessfulDelivery(payload);
    });
  }

  function sendClickToGoogleSheet(sheetData) {
    if (!PRODUCT_CLICK_TRACKING_ENABLED) return;
    initProductClickDetectedCountry();
    if (!PRODUCT_CLICK_COUNTRY_READY) {
      PRODUCT_CLICK_PENDING_SHEETS.push(sheetData);
      return;
    }
    if (!deliverSheetPayload(sheetData)) return;
    markPdpViewAfterSuccessfulDelivery(sheetData);
  }

  function buildIdentityFields() {
    return {
      visitor_id: getVisitorId(),
      session_id: getSessionId(),
      tracking_version: TRACKING_VERSION,
    };
  }

  // ── Ecommerce funnel (Phase 3A) ───────────────────────────────────────────

  var PDP_VIEW_DEDUP_PREFIX = "autelpilot_pdp_view_";
  var pdpViewTrackingInitialized = false;
  var ECOMMERCE_EVENT_TYPES = {
    productPageView: "product_page_view",
    addToCart: "add_to_cart",
    checkoutClick: "checkout_click",
    buyNowClick: "buy_now_click",
  };

  function getShopifyRootPath() {
    return (
      (window.Shopify &&
        window.Shopify.routes &&
        window.Shopify.routes.root) ||
      "/"
    );
  }

  function getProductHandleFromPath(pathname) {
    var path = pathname || window.location.pathname || "";
    var match = path.match(/\/products\/([^/?#]+)/i);
    return match ? decodeURIComponent(match[1]) : null;
  }

  function parseJsonSafe(text) {
    try {
      return JSON.parse(text);
    } catch (err) {
      return null;
    }
  }

  function readProductJsonFromDom() {
    var selectors = [
      'script[type="application/json"][data-product-json]',
      'script[type="application/json"][id*="ProductJson"]',
      'script[type="application/json"][data-product]',
    ];
    for (var i = 0; i < selectors.length; i++) {
      var el = document.querySelector(selectors[i]);
      if (!el || !el.textContent) continue;
      var parsed = parseJsonSafe(el.textContent.trim());
      if (parsed && (parsed.id || parsed.handle)) return parsed;
    }
    return null;
  }

  function resolveProductContext() {
    var handle = getProductHandleFromPath();
    var productId = null;
    var variantId = null;
    var productTitle = null;

    try {
      var analyticsMeta =
        window.ShopifyAnalytics &&
        window.ShopifyAnalytics.meta &&
        window.ShopifyAnalytics.meta.product;
      if (analyticsMeta) {
        if (analyticsMeta.id != null) productId = String(analyticsMeta.id);
        if (analyticsMeta.title) productTitle = String(analyticsMeta.title);
      }
      var selectedVariantId =
        window.ShopifyAnalytics &&
        window.ShopifyAnalytics.meta &&
        window.ShopifyAnalytics.meta.selectedVariantId;
      if (selectedVariantId != null) variantId = String(selectedVariantId);
    } catch (err) {}

    var productJson = readProductJsonFromDom();
    if (productJson) {
      if (productJson.id != null) productId = String(productJson.id);
      if (productJson.handle) handle = handle || String(productJson.handle);
      if (productJson.title) productTitle = productTitle || String(productJson.title);
      if (!variantId && productJson.selected_or_first_available_variant) {
        variantId = String(productJson.selected_or_first_available_variant.id);
      }
    }

    var variantInput = document.querySelector(
      'form[action*="/cart/add"] input[name="id"]',
    );
    if (variantInput && variantInput.value) {
      variantId = String(variantInput.value);
    }

    return {
      product_handle: handle,
      product_id: productId,
      variant_id: variantId,
      product_title: productTitle,
    };
  }

  // Phase 3B-2 cart bridge attribute keys (underscore prefix → order note_attributes)
  var CART_ATTR_SESSION_KEY = "_autelpilot_session_id";
  var CART_ATTR_VISITOR_KEY = "_autelpilot_visitor_id";
  var CART_TOKEN_RETRY_DELAY_MS = 200;

  // Cart attributes are cart-level, last-write-wins fallbacks for order identity.
  // Line-item attribution in Phase 3B-4 uses cart_token + variant_id → ATC events.
  function isValidTrackingUuid(value) {
    if (!value || typeof value !== "string") return false;
    return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(
      value.trim(),
    );
  }

  function debugCartAttribution(label, detail) {
    if (!NAV_TRACKING_DEBUG) return;
    if (typeof console !== "undefined" && console.info) {
      console.info(label, detail);
    }
  }

  function fetchCartJson(attempt, callback) {
    var cartUrl = getShopifyRootPath() + "cart.js";
    fetch(cartUrl, { credentials: "same-origin" })
      .then(function (res) {
        if (res.status >= 400 && res.status < 500) {
          var clientErr = new Error("cart.js client error");
          clientErr.noRetry = true;
          throw clientErr;
        }
        if (!res.ok) throw new Error("cart.js failed");
        return res.json();
      })
      .then(function (data) {
        cacheCartSnapshot(data);
        callback(data || null);
      })
      .catch(function (err) {
        if (attempt < 1 && !err.noRetry) {
          setTimeout(function () {
            fetchCartJson(attempt + 1, callback);
          }, CART_TOKEN_RETRY_DELAY_MS);
          return;
        }
        callback(null);
      });
  }

  function writeCartAttributionAttributes(identity, callback) {
    var sessionId = identity && identity.session_id;
    var visitorId = identity && identity.visitor_id;
    if (!isValidTrackingUuid(sessionId) || !isValidTrackingUuid(visitorId)) {
      debugCartAttribution("[CART ATTRIBUTE WRITE]", "skipped invalid identity");
      if (callback) callback(false);
      return;
    }

    var updateUrl = getShopifyRootPath() + "cart/update.js";
    var payload = { attributes: {} };
    payload.attributes[CART_ATTR_SESSION_KEY] = sessionId;
    payload.attributes[CART_ATTR_VISITOR_KEY] = visitorId;

    fetch(updateUrl, {
      method: "POST",
      credentials: "same-origin",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify(payload),
    })
      .then(function (res) {
        debugCartAttribution(
          "[CART ATTRIBUTE WRITE]",
          res.ok ? "success" : "failed status=" + res.status,
        );
        if (callback) callback(!!res.ok);
      })
      .catch(function () {
        debugCartAttribution("[CART ATTRIBUTE WRITE]", "failed network");
        if (callback) callback(false);
      });
  }

  function sendEcommerceEvent(payload) {
    if (!PRODUCT_CLICK_TRACKING_ENABLED) return;
    var eventPayload = Object.assign(
      {
        page_title: getClickPageTitle(),
        device_type: getDeviceType(),
        timestamp: new Date().toISOString(),
      },
      buildIdentityFields(),
      payload,
    );
    debugNavEvent(eventPayload);
    sendClickToGoogleSheet(eventPayload);
  }

  function hasPdpViewForHandle(handle) {
    try {
      return sessionStorage.getItem(PDP_VIEW_DEDUP_PREFIX + handle) === "1";
    } catch (err) {
      return false;
    }
  }

  function markPdpViewForHandle(handle) {
    try {
      sessionStorage.setItem(PDP_VIEW_DEDUP_PREFIX + handle, "1");
    } catch (err) {}
  }

  function emitProductPageView() {
    var ctx = resolveProductContext();
    if (!ctx.product_handle) return;
    if (hasPdpViewForHandle(ctx.product_handle)) return;

    sendEcommerceEvent({
      event_type: ECOMMERCE_EVENT_TYPES.productPageView,
      click_target_url: "/products/" + ctx.product_handle,
      product_handle: ctx.product_handle,
      product_id: ctx.product_id,
      variant_id: ctx.variant_id,
      click_module_location: "product-page",
    });
  }

  function normalizeCartAddUrl(url) {
    if (!url) return "";
    if (typeof url === "string") return url;
    if (url.url) return String(url.url);
    return String(url);
  }

  function isCartAddRequest(url, method) {
    var normalized = normalizeCartAddUrl(url).toLowerCase();
    var verb = (method || "GET").toUpperCase();
    if (verb !== "POST") return false;
    return normalized.indexOf("/cart/add") !== -1;
  }

  function extractVariantFromCartAddBody(body) {
    var variantId = null;
    var quantity = 1;
    if (!body) return { variant_id: variantId, quantity: quantity };

    if (typeof FormData !== "undefined" && body instanceof FormData) {
      if (body.get("id")) variantId = String(body.get("id"));
      if (body.get("quantity")) quantity = Number(body.get("quantity")) || 1;
      return { variant_id: variantId, quantity: quantity };
    }

    if (typeof body === "string") {
      var params = new URLSearchParams(body);
      if (params.get("id")) variantId = String(params.get("id"));
      if (params.get("quantity")) quantity = Number(params.get("quantity")) || 1;
      var jsonParsed = parseJsonSafe(body);
      if (jsonParsed) {
        if (jsonParsed.id != null) variantId = String(jsonParsed.id);
        if (jsonParsed.quantity != null) quantity = Number(jsonParsed.quantity) || 1;
      }
      return { variant_id: variantId, quantity: quantity };
    }

    if (typeof body === "object") {
      if (body.id != null) variantId = String(body.id);
      if (body.quantity != null) quantity = Number(body.quantity) || 1;
    }

    return { variant_id: variantId, quantity: quantity };
  }

  function emitAddToCartSuccess(input) {
    var ctx = resolveProductContext();
    var variantId = input.variant_id || ctx.variant_id;
    var quantity = input.quantity || 1;
    var identity = buildIdentityFields();

    fetchCartJson(0, function (cartData) {
      var cartToken =
        cartData && cartData.token ? String(cartData.token) : null;
      debugCartAttribution("[CART TOKEN]", cartToken ? "present" : "null");

      // Non-blocking — attribute write failure must not affect checkout flow.
      writeCartAttributionAttributes(identity, function () {});

      sendEcommerceEvent({
        event_type: ECOMMERCE_EVENT_TYPES.addToCart,
        click_target_url: ctx.product_handle
          ? "/products/" + ctx.product_handle
          : window.location.pathname,
        product_handle: ctx.product_handle,
        product_id: ctx.product_id,
        variant_id: variantId,
        cart_token: cartToken,
        click_module_location: "product-buy",
        click_element_text: "add_to_cart_success",
        quantity: quantity,
      });
    });
  }

  function initProductPageViewTracking() {
    if (!getProductHandleFromPath()) return;
    if (pdpViewTrackingInitialized) return;
    pdpViewTrackingInitialized = true;
    emitProductPageView();
  }

  function initAddToCartTracking() {
    if (window.__autelpilotCartAddPatched) return;
    window.__autelpilotCartAddPatched = true;

    if (typeof window.fetch === "function") {
      var nativeFetch = window.fetch.bind(window);
      window.fetch = function (input, init) {
        var url = normalizeCartAddUrl(input);
        var method = (init && init.method) || "GET";
        var body = init && init.body;
        return nativeFetch(input, init).then(function (response) {
          if (isCartAddRequest(url, method) && response && response.ok) {
            var parsed = extractVariantFromCartAddBody(body);
            emitAddToCartSuccess(parsed);
          }
          return response;
        });
      };
    }

    if (typeof XMLHttpRequest !== "undefined") {
      var nativeOpen = XMLHttpRequest.prototype.open;
      var nativeSend = XMLHttpRequest.prototype.send;
      XMLHttpRequest.prototype.open = function (method, url) {
        this.__autelpilotMethod = method;
        this.__autelpilotUrl = url;
        return nativeOpen.apply(this, arguments);
      };
      XMLHttpRequest.prototype.send = function (body) {
        var xhr = this;
        var method = xhr.__autelpilotMethod;
        var url = xhr.__autelpilotUrl;
        xhr.addEventListener("load", function () {
          if (
            xhr.status >= 200 &&
            xhr.status < 300 &&
            isCartAddRequest(url, method)
          ) {
            emitAddToCartSuccess(extractVariantFromCartAddBody(body));
          }
        });
        return nativeSend.apply(this, arguments);
      };
    }
  }

  // ── Checkout Intent (Phase 3C) ─────────────────────────────────────────────

  var CHECKOUT_DEDUP_MS = 1500;
  var checkoutActionDedup = {};
  var lastKnownCartSnapshot = null;

  function cacheCartSnapshot(data) {
    if (!data) return;
    lastKnownCartSnapshot = {
      token: data.token ? String(data.token) : null,
      item_count:
        data.item_count != null ?
          Number(data.item_count)
        : Array.isArray(data.items) ?
          data.items.length
        : null,
      cached_at: Date.now(),
    };
  }

  function checkoutDedupKey(eventType, source, variantId) {
    return eventType + "|" + (source || "other") + "|" + (variantId || "");
  }

  function shouldSkipCheckoutDedup(key) {
    var now = Date.now();
    var last = checkoutActionDedup[key];
    if (last && now - last < CHECKOUT_DEDUP_MS) return true;
    checkoutActionDedup[key] = now;
    return false;
  }

  function debugCheckoutIntent(label, detail) {
    if (!NAV_TRACKING_DEBUG) return;
    if (typeof console !== "undefined" && console.info) {
      console.info(label, detail);
    }
  }

  function getPagePath() {
    return window.location.pathname || "/";
  }

  function resolveCheckoutSource(el) {
    if (!el || !el.closest) return "other";
    if (el.closest(".buyMe, .sticky-cart, [data-sticky-cart]")) {
      return "sticky_cart";
    }
    if (
      el.closest(
        "#CartDrawer, #cart-drawer, cart-drawer, [id*='CartDrawer'], [id*='cart-drawer'], [class*='cart-drawer'], [data-cart-drawer], cart-notification, #cart-notification, .cart-notification, #cart-notification-form",
      )
    ) {
      return "cart_drawer";
    }
    if (window.location.pathname.indexOf("/cart") === 0) return "cart_page";
    return "other";
  }

  function findCheckoutNamedControl(form) {
    if (!form || !form.querySelector) return null;
    var inForm = form.querySelector(
      'button[name="checkout"], input[name="checkout"]',
    );
    if (inForm) return inForm;
    var formId = form.id;
    if (!formId) return null;
    return document.querySelector(
      'button[name="checkout"][form="' +
        formId +
        '"], input[name="checkout"][form="' +
        formId +
        '"]',
    );
  }

  function isCheckoutIntentElement(el) {
    if (!el || !el.closest) return false;
    if (el.closest('button[name="checkout"], input[name="checkout"]')) {
      return true;
    }
    var checkoutLink = el.closest('a[href*="/checkout"]');
    if (
      checkoutLink &&
      checkoutLink.closest(
        "#CartDrawer, #cart-drawer, cart-drawer, [id*='CartDrawer'], [class*='cart-drawer'], .cart-drawer, cart-notification, #cart-notification, .cart-notification, .buyMe, form[action='/cart'], form[action*='/cart'], .cart, .cart__contents, main",
      )
    ) {
      return true;
    }
    if (
      el.closest(
        ".cart__checkout-button, .cart-drawer__checkout, .cart__ctas button[type='submit'], .drawer__footer button[name='checkout'], #cart-notification-form button[name='checkout']",
      )
    ) {
      return true;
    }
    return false;
  }

  function isBuyNowIntentElement(el) {
    if (!el || !el.closest) return false;
    if (
      el.closest(
        ".shopify-payment-button, .shopify-payment-button__button, .additional-checkout-buttons, [data-shopify='payment-button']",
      )
    ) {
      return true;
    }
    var btn = el.closest(
      ".product-form-buyBtns button, .product-form button, .buyMe button",
    );
    if (btn) {
      if (
        btn.matches(".product-form__submit") ||
        btn.classList.contains("product-form__submit")
      ) {
        return false;
      }
      var text = (btn.textContent || btn.innerText || "").trim().toLowerCase();
      if (text.indexOf("buy now") !== -1) return true;
    }
    return false;
  }

  function readProductQuantity() {
    var input = document.querySelector(
      'form[action*="/cart/add"] input[name="quantity"], form[action*="/cart/add"] select[name="quantity"]',
    );
    if (!input) return 1;
    var value = Number(input.value);
    return Number.isFinite(value) && value > 0 ? value : 1;
  }

  function readProductPriceCurrency(ctx) {
    var price = null;
    var currency = null;
    try {
      if (
        window.Shopify &&
        window.Shopify.currency &&
        window.Shopify.currency.active
      ) {
        currency = String(window.Shopify.currency.active);
      }
    } catch (err) {}
    try {
      var productJson = readProductJsonFromDom();
      if (productJson && Array.isArray(productJson.variants) && ctx.variant_id) {
        for (var i = 0; i < productJson.variants.length; i++) {
          var variant = productJson.variants[i];
          if (String(variant.id) === String(ctx.variant_id)) {
            price = variant.price != null ? String(variant.price) : null;
            break;
          }
        }
      }
    } catch (err2) {}
    return { price: price, currency: currency };
  }

  function sendEcommerceEventReliable(payload) {
    if (!PRODUCT_CLICK_TRACKING_ENABLED) return;
    var eventPayload = Object.assign(
      {
        page_title: getClickPageTitle(),
        device_type: getDeviceType(),
        timestamp: new Date().toISOString(),
        click_target_url: getPagePath(),
      },
      buildIdentityFields(),
      payload,
    );
    debugCheckoutIntent("[CHECKOUT INTENT]", {
      event_type: eventPayload.event_type,
      checkout_source: eventPayload.checkout_source,
    });
    var endpoint = PRODUCT_CLICK_SUPABASE_ENDPOINT;
    if (endpoint) {
      try {
        if (
          typeof navigator !== "undefined" &&
          typeof navigator.sendBeacon === "function"
        ) {
          var blob = new Blob([JSON.stringify(eventPayload)], {
            type: "application/json",
          });
          if (navigator.sendBeacon(endpoint, blob)) return;
        }
      } catch (beaconErr) {}
      postSupabasePayload(eventPayload);
      return;
    }
    if (PRODUCT_CLICK_SHEET_ENDPOINT) {
      postSheetPayload(eventPayload);
    }
  }

  function emitCheckoutClick(source) {
    var dedupKey = checkoutDedupKey("checkout_click", source, "");
    if (shouldSkipCheckoutDedup(dedupKey)) return;

    var snapshot = lastKnownCartSnapshot;
    sendEcommerceEventReliable({
      event_type: ECOMMERCE_EVENT_TYPES.checkoutClick,
      checkout_source: source,
      cart_token: snapshot && snapshot.token ? snapshot.token : null,
      cart_item_count:
        snapshot && snapshot.item_count != null ? snapshot.item_count : null,
      click_module_location:
        source === "cart_drawer" ?
          "cart-drawer"
        : source === "sticky_cart" ?
          "sticky-cart"
        : "cart-page",
      click_element_text: "checkout_click",
    });
  }

  function emitBuyNowClick() {
    var ctx = resolveProductContext();
    var dedupKey = checkoutDedupKey(
      "buy_now_click",
      "pdp_buy_now",
      ctx.variant_id || "",
    );
    if (shouldSkipCheckoutDedup(dedupKey)) return;

    var pricing = readProductPriceCurrency(ctx);
    sendEcommerceEventReliable({
      event_type: ECOMMERCE_EVENT_TYPES.buyNowClick,
      checkout_source: "pdp_buy_now",
      product_handle: ctx.product_handle,
      product_id: ctx.product_id,
      variant_id: ctx.variant_id,
      quantity: readProductQuantity(),
      price: pricing.price,
      currency: pricing.currency,
      click_module_location: "product-buy",
      click_element_text: "buy_now_click",
    });
  }

  function resolveCheckoutIntentTarget(rawTarget) {
    var target = rawTarget;
    if (!target || target.nodeType !== 1) {
      target = target && target.parentElement ? target.parentElement : null;
    }
    return target;
  }

  function handleCheckoutIntentInteraction(event) {
    var target = resolveCheckoutIntentTarget(event.target);
    if (!target) return;

    if (isBuyNowIntentElement(target)) {
      emitBuyNowClick();
      return;
    }
    if (isCheckoutIntentElement(target)) {
      emitCheckoutClick(resolveCheckoutSource(target));
    }
  }

  function initCheckoutIntentTracking() {
    if (window.__autelpilotCheckoutIntentBound) return;
    window.__autelpilotCheckoutIntentBound = true;

    document.addEventListener("click", handleCheckoutIntentInteraction, true);
    // Mobile touch often navigates before the delayed click event; capture earlier.
    if (typeof window.PointerEvent === "function") {
      document.addEventListener("pointerdown", handleCheckoutIntentInteraction, true);
    } else {
      document.addEventListener("touchend", handleCheckoutIntentInteraction, true);
    }
    document.addEventListener(
      "submit",
      function (event) {
        var form = event.target;
        if (!form || form.nodeName !== "FORM") return;
        var checkoutControl = null;
        var submitter = event.submitter;
        if (
          submitter &&
          submitter.getAttribute &&
          submitter.getAttribute("name") === "checkout"
        ) {
          checkoutControl = submitter;
        } else {
          checkoutControl = findCheckoutNamedControl(form);
        }
        if (!checkoutControl) return;
        if (!isCheckoutIntentElement(checkoutControl)) return;
        emitCheckoutClick(resolveCheckoutSource(checkoutControl));
      },
      true,
    );
  }

  function initEcommerceFunnelTracking() {
    initProductPageViewTracking();
    initAddToCartTracking();
    initCheckoutIntentTracking();
  }

  // ── Navigation item impression (Phase 1) ─────────────────────────────────

  var impressionDedupSet = null;
  var impressionDedupHydrated = false;
  var mobileImpressionObserver = null;
  var mobileImpressionObserverRoot = null;

  function normalizeStructuralKey(value) {
    return String(value || "")
      .trim()
      .toLowerCase()
      .replace(/\s+/g, "-")
      .replace(/[^a-z0-9._-]+/g, "-")
      .replace(/-+/g, "-")
      .replace(/^-|-$/g, "");
  }

  function isContainerDomId(id) {
    return (
      /^Details-menu-/i.test(id) ||
      /^panel-/i.test(id) ||
      /^MegaMenu-/i.test(id) ||
      /^Details-HeaderMenu-/i.test(id)
    );
  }

  function isItemLevelDomId(id) {
    if (!id || isContainerDomId(id)) return false;
    return (
      /^HeaderMenu-[a-z0-9-]+$/i.test(id) ||
      /^HeaderDrawer-[a-z0-9-]+$/i.test(id)
    );
  }

  function resolveItemLevelDomNodeId(el) {
    if (!el) return null;
    if (el.dataset && el.dataset.navigationNodeId) {
      return String(el.dataset.navigationNodeId);
    }
    if (el.id && isItemLevelDomId(el.id)) {
      return el.id;
    }
    return null;
  }

  function buildNavigationItemKey(fields) {
    var root = fields.navigation_root || "_";
    var panel = fields.panel_id || "_";
    var level = String(fields.menu_level);
    if (fields.node_id) return "node:" + fields.node_id;
    if (fields.click_target_url) {
      return root + "|" + panel + "|" + level + "|url:" + fields.click_target_url;
    }
    var structural = fields.structural_key
      ? normalizeStructuralKey(fields.structural_key)
      : "_";
    return root + "|" + panel + "|" + level + "|struct:" + structural;
  }

  function hydrateImpressionDedupSet() {
    if (impressionDedupHydrated) return impressionDedupSet;
    impressionDedupSet = new Set();
    try {
      var raw = sessionStorage.getItem(IMPRESSION_DEDUP_STORAGE_KEY);
      if (raw) {
        var list = JSON.parse(raw);
        if (Array.isArray(list)) {
          for (var i = 0; i < list.length; i++) impressionDedupSet.add(list[i]);
        }
      }
    } catch (err) {}
    impressionDedupHydrated = true;
    return impressionDedupSet;
  }

  function persistImpressionDedupSet() {
    try {
      var set = hydrateImpressionDedupSet();
      var list = Array.from(set);
      if (list.length > 5000) list = list.slice(list.length - 5000);
      sessionStorage.setItem(IMPRESSION_DEDUP_STORAGE_KEY, JSON.stringify(list));
    } catch (err) {}
  }

  function hasSentItemImpression(key) {
    return hydrateImpressionDedupSet().has(key);
  }

  function saveImpressionDedupKey(key) {
    var set = hydrateImpressionDedupSet();
    if (set.has(key)) return false;
    set.add(key);
    persistImpressionDedupSet();
    return true;
  }

  function isDevNavImpressionDebug() {
    if (NAV_TRACKING_DEBUG) return true;
    try {
      var host = window.location && window.location.hostname;
      return /localhost|127\.0\.0\.1|preview/i.test(host || "");
    } catch (err) {
      return false;
    }
  }

  function debugNavImpression(payload, meta) {
    if (!isDevNavImpressionDebug()) return;
    if (typeof console === "undefined" || !console.info) return;
    if (meta && meta.dedupHit) {
      console.info("[NAV IMPRESSION DEDUP]", {
        label: payload.click_element_text,
        key: payload.navigation_item_key,
        root: payload.navigation_root,
        panel: payload.panel_id,
        level: payload.menu_level,
        url: payload.click_target_url,
        session_id: payload.session_id,
      });
      return;
    }
    console.info("[NAV IMPRESSION]", {
      label: payload.click_element_text,
      key: payload.navigation_item_key,
      root: payload.navigation_root,
      panel: payload.panel_id,
      level: payload.menu_level,
      url: payload.click_target_url,
      observerRoot: meta && meta.observerRoot ? meta.observerRoot : null,
      visibilityRatio:
        meta && typeof meta.visibilityRatio === "number"
          ? meta.visibilityRatio
          : null,
      dedupHit: false,
      session_id: payload.session_id,
      device: payload.device_type,
    });
  }

  function sendNavigationItemImpression(itemFields, debugMeta) {
    var nodeId = itemFields.node_id || resolveItemLevelDomNodeId(itemFields._clickableEl || null);
    var key =
      itemFields.navigation_item_key ||
      buildNavigationItemKey({
        navigation_root: itemFields.navigation_root,
        panel_id: itemFields.panel_id,
        menu_level: itemFields.menu_level,
        click_target_url: itemFields.click_target_url,
        structural_key: itemFields.structural_key,
        node_id: nodeId,
      });

    if (hasSentItemImpression(key)) {
      if (isDevNavImpressionDebug()) {
        debugNavImpression(
          {
            click_element_text: itemFields.click_element_text,
            navigation_item_key: key,
            navigation_root: itemFields.navigation_root,
            panel_id: itemFields.panel_id,
            menu_level: itemFields.menu_level,
            click_target_url: itemFields.click_target_url,
            session_id: getSessionId(),
          },
          { dedupHit: true },
        );
      }
      return;
    }
    saveImpressionDedupKey(key);

    var payload = {
      event_type: "navigation_item_impression",
      navigation_root: itemFields.navigation_root,
      panel_id: itemFields.panel_id,
      menu_level: itemFields.menu_level,
      click_target_url: itemFields.click_target_url,
      click_element_text: itemFields.click_element_text,
      navigation_item_key: key,
      is_view_all: itemFields.is_view_all,
      click_element_selector: "nav-track:navigation_item_impression:" + key,
      click_element_dom_tree: buildSyntheticNavDomTree(
        "navigation_item_impression",
        itemFields.navigation_root,
        itemFields.panel_id,
      ),
      click_raw_target: "navigation",
    };

    var enriched = Object.assign(
      {
        page_title: getClickPageTitle(),
        device_type: getDeviceType(),
        timestamp: new Date().toISOString(),
        click_module_location: "header",
      },
      buildIdentityFields(),
      payload,
    );
    debugNavImpression(enriched, debugMeta);
    sendClickToGoogleSheet(enriched);
  }

  function getVisibleRect(el) {
    if (!el || !el.getBoundingClientRect) return null;
    return el.getBoundingClientRect();
  }

  function computeVisibleRatioInContainer(elRect, containerRect) {
    var overlapLeft = Math.max(elRect.left, containerRect.left);
    var overlapTop = Math.max(elRect.top, containerRect.top);
    var overlapRight = Math.min(elRect.right, containerRect.right);
    var overlapBottom = Math.min(elRect.bottom, containerRect.bottom);
    var overlapW = Math.max(0, overlapRight - overlapLeft);
    var overlapH = Math.max(0, overlapBottom - overlapTop);
    var elArea = elRect.width * elRect.height;
    if (elArea <= 0) return 0;
    return (overlapW * overlapH) / elArea;
  }

  function isElementVisibleInContainer(el, container) {
    if (!isElementVisible(el)) return false;
    var elRect = getVisibleRect(el);
    if (!elRect || elRect.width <= 0 || elRect.height <= 0) return false;
    if (!container) return true;
    var containerRect = getVisibleRect(container);
    if (!containerRect) return false;
    return (
      computeVisibleRatioInContainer(elRect, containerRect) >=
      NAV_IMPRESSION_THRESHOLD
    );
  }

  function collectDesktopL2Impressions(headerMenu, navigationRoot, contentEl) {
    headerMenu
      .querySelectorAll("li.mega-menu__level2-item[data-target]")
      .forEach(function (l2) {
        if (!isElementVisibleInContainer(l2, contentEl)) return;
        var panelId = l2.dataset && l2.dataset.target;
        if (!panelId) return;
        var anchor = l2.querySelector("a[href]") || l2;
        var url = resolveClickTargetUrl(anchor, anchor);
        var text = getClickText(anchor, anchor) || panelId;
        sendNavigationItemImpression({
          navigation_root: navigationRoot,
          panel_id: panelId,
          menu_level: 2,
          click_target_url: url,
          click_element_text: text,
          is_view_all: false,
          structural_key: panelId,
          _clickableEl: anchor,
        });
      });
  }

  function collectDesktopPanelProductImpressions(
    headerMenu,
    navigationRoot,
    contentEl,
  ) {
    var activePanel = headerMenu.querySelector(".mega-menu__panel.is-active[id]");
    if (!activePanel) return;
    if (!isPanelActiveVisible(activePanel.id)) return;
    if (!isElementVisibleInContainer(activePanel, contentEl)) return;
    var panelId = activePanel.id;

    activePanel
      .querySelectorAll("a.mega-menu__product-item")
      .forEach(function (anchor) {
        if (!isElementVisibleInContainer(anchor, contentEl)) return;
        var isViewAll =
          anchor.classList &&
          anchor.classList.contains("mega-menu__product-item--view-all");
        var url = resolveClickTargetUrl(anchor, anchor);
        var text = getClickText(anchor, anchor);
        sendNavigationItemImpression({
          navigation_root: navigationRoot,
          panel_id: panelId,
          menu_level: 3,
          click_target_url: url,
          click_element_text: text,
          is_view_all: isViewAll,
          structural_key: url || text,
          _clickableEl: anchor,
        });
      });
  }

  function scanDesktopMenuImpressions(headerMenu, navigationRoot) {
    if (!ENABLE_NAVIGATION_EXPOSURE_EVENTS) return;
    if (getDeviceType() !== "desktop") return;
    if (!isMegaMenuOpen(headerMenu)) return;
    var contentEl = headerMenu.querySelector(".mega-menu__content");
    if (!isMegaMenuContentUserVisible(contentEl)) return;
    collectDesktopL2Impressions(headerMenu, navigationRoot, contentEl);
    collectDesktopPanelProductImpressions(headerMenu, navigationRoot, contentEl);
  }

  function isMobileDrawerOpen() {
    var detailsEl = document.querySelector(
      "details#Details-menu-drawer-container, details.menu-drawer-container",
    );
    return !!(detailsEl && detailsEl.open);
  }

  function isScrollableDrawerRoot(el) {
    if (!el || !window.getComputedStyle) return false;
    var style = window.getComputedStyle(el);
    var oy = style.overflowY;
    var scrollableOverflow =
      oy === "auto" || oy === "scroll" || oy === "overlay";
    return scrollableOverflow && el.scrollHeight > el.clientHeight + 1;
  }

  function describeMobileScrollRoot(el) {
    if (!el) return null;
    var className = getElementClassName(el).trim();
    return {
      selector: el.id
        ? "#" + el.id
        : className
          ? "." + className.split(/\s+/).join(".")
          : el.tagName.toLowerCase(),
      id: el.id || null,
      className: className || null,
    };
  }

  function findMobileDrawerScrollRoot() {
    var drawer = document.getElementById("menu-drawer");
    if (!drawer) return null;
    var candidates = [
      drawer.querySelector(".menu-drawer__inner"),
      drawer.querySelector(".menu-drawer__navigation"),
      drawer,
    ];
    for (var i = 0; i < candidates.length; i++) {
      if (candidates[i] && isScrollableDrawerRoot(candidates[i])) {
        return candidates[i];
      }
    }
    return candidates[0] || drawer;
  }

  function getMobileDrawerScrollRoot() {
    return findMobileDrawerScrollRoot();
  }

  function isMobileItemExpanded(el) {
    if (!isMobileDrawerOpen()) return false;
    var drawer = document.getElementById("menu-drawer");
    if (!drawer || !drawer.contains(el)) return false;
    var current = el.parentElement;
    while (current && current !== drawer) {
      if (current.tagName === "DETAILS" && !current.open) return false;
      current = current.parentElement;
    }
    return true;
  }

  function isElementVisibleInScrollRoot(el, root) {
    if (!isElementVisible(el)) return false;
    if (!root) return true;
    return isElementVisibleInContainer(el, root);
  }

  function isMobileItemInActiveView(el) {
    if (!isMobileItemExpanded(el)) return false;
    var root = findMobileDrawerScrollRoot();
    if (!root) return false;
    return isElementVisibleInScrollRoot(el, root);
  }

  function resolveMobileImpressionFromElement(el) {
    var drawer = document.getElementById("menu-drawer");
    if (!drawer || !drawer.contains(el)) return null;

    var isProduct =
      el.matches && el.matches("a.menu-drawer__product-item");
    var inLevel3 =
      el.closest && el.closest(".menu-drawer__menu--level3");
    var isViewAll =
      el.classList &&
      el.classList.contains("menu-drawer__product-item--view-all");
    var isL2Summary =
      el.matches &&
      (el.matches("summary.menu-drawer__menu-item--level2") ||
        (el.id && el.id.indexOf("HeaderDrawer-") === 0));
    var section =
      el.closest && el.closest('details[id^="Details-menu-section-"]');
    var isL1Summary =
      el.matches &&
      el.matches("summary") &&
      section &&
      (el.closest(".menu-drawer__menu--level1") ||
        el.closest(".menu-drawer__section-summary"));

    if (isProduct || (inLevel3 && el.matches && el.matches("a[href]"))) {
      var productCtx = getNavigationContext(el, el);
      return {
        navigation_root: productCtx.navigation_root,
        panel_id: productCtx.panel_id,
        menu_level: 3,
        click_target_url: productCtx.click_target_url,
        click_element_text: getClickText(el, el),
        is_view_all: !!isViewAll,
        structural_key: productCtx.click_target_url || getClickText(el, el),
        _clickableEl: el,
      };
    }

    if (isL2Summary) {
      var l2Ctx = getNavigationContext(el, el);
      return {
        navigation_root: l2Ctx.navigation_root,
        panel_id: l2Ctx.panel_id,
        menu_level: 2,
        click_target_url: l2Ctx.click_target_url,
        click_element_text: getClickText(el, el),
        is_view_all: false,
        structural_key: l2Ctx.panel_id || el.id,
        _clickableEl: el,
      };
    }

    if (isL1Summary && section && section.id) {
      var root = section.id.replace(/^Details-menu-section-/, "");
      return {
        navigation_root: root,
        panel_id: null,
        menu_level: 1,
        click_target_url: resolveClickTargetUrl(el, el),
        click_element_text: getClickText(el, el),
        is_view_all: false,
        structural_key: section.id,
        _clickableEl: el,
      };
    }

    return null;
  }

  function handleMobileImpressionIntersection(entries) {
    if (getDeviceType() !== "mobile") return;
    var rootEl = findMobileDrawerScrollRoot();
    var rootMeta = describeMobileScrollRoot(rootEl);
    for (var i = 0; i < entries.length; i++) {
      var entry = entries[i];
      if (!entry.isIntersecting) continue;
      if (entry.intersectionRatio < NAV_IMPRESSION_THRESHOLD) continue;
      var el = entry.target;
      if (!isMobileItemInActiveView(el)) continue;
      var desc = resolveMobileImpressionFromElement(el);
      if (!desc) continue;
      sendNavigationItemImpression(desc, {
        observerRoot: rootMeta ? rootMeta.selector : null,
        visibilityRatio: entry.intersectionRatio,
      });
    }
  }

  function ensureMobileImpressionObserver() {
    var root = findMobileDrawerScrollRoot();
    if (mobileImpressionObserver && mobileImpressionObserverRoot === root) {
      return mobileImpressionObserver;
    }
    resetMobileImpressionObserver();
    mobileImpressionObserverRoot = root;
    mobileImpressionObserver = new IntersectionObserver(
      handleMobileImpressionIntersection,
      {
        root: root,
        threshold: [NAV_IMPRESSION_THRESHOLD],
      },
    );
    return mobileImpressionObserver;
  }

  function registerMobileImpressionTarget(el) {
    if (!el || el.dataset.navImpressionObserved === "1") return;
    if (getDeviceType() !== "mobile") return;
    el.dataset.navImpressionObserved = "1";
    ensureMobileImpressionObserver().observe(el);
  }

  function scanMobileImpressionTargets() {
    if (!ENABLE_NAVIGATION_EXPOSURE_EVENTS) return;
    if (getDeviceType() !== "mobile") return;
    if (!isMobileDrawerOpen()) return;
    var drawer = document.getElementById("menu-drawer");
    if (!drawer) return;

    drawer
      .querySelectorAll(
        '.menu-drawer__menu--level1 details[id^="Details-menu-section-"] > summary',
      )
      .forEach(registerMobileImpressionTarget);

    drawer
      .querySelectorAll(
        'details[id^="Details-menu-section-"][open] summary.menu-drawer__menu-item--level2, details[id^="Details-menu-section-"][open] summary[id^="HeaderDrawer-"]',
      )
      .forEach(registerMobileImpressionTarget);

    drawer
      .querySelectorAll(
        'details[id^="Details-menu-drawer-"][open]:not(#Details-menu-drawer-container):not(.menu-drawer-container) a.menu-drawer__product-item',
      )
      .forEach(registerMobileImpressionTarget);
  }

  function resetMobileImpressionObserver() {
    if (mobileImpressionObserver) {
      mobileImpressionObserver.disconnect();
      mobileImpressionObserver = null;
      mobileImpressionObserverRoot = null;
    }
    var drawer = document.getElementById("menu-drawer");
    if (!drawer) return;
    drawer.querySelectorAll("[data-nav-impression-observed]").forEach(function (el) {
      delete el.dataset.navImpressionObserved;
    });
  }

  function initMobileImpressionTracking() {
    var container = document.querySelector(
      "details#Details-menu-drawer-container, details.menu-drawer-container",
    );
    if (!container || container.dataset.navImpressionBound === "1") return;
    container.dataset.navImpressionBound = "1";

    container.addEventListener("toggle", function () {
      if (!container.open) return;
      resetMobileImpressionObserver();
      setTimeout(scanMobileImpressionTargets, 50);
    });

    var drawer = document.getElementById("menu-drawer");
    if (drawer) {
      drawer.addEventListener(
        "toggle",
        function () {
          resetMobileImpressionObserver();
          setTimeout(scanMobileImpressionTargets, 50);
        },
        true,
      );
    }
  }

  function buildSyntheticNavDomTree(kind, root, panelId) {
    return (
      "HTMLElement: html > body > nav-track#" +
      kind +
      (root ? " > root#" + root : "") +
      (panelId ? " > panel#" + panelId : "")
    );
  }

  function debugNavEvent(payload) {
    if (!NAV_TRACKING_DEBUG) return;
    var debugPayload = {
      event_type: payload.event_type,
      navigation_root: payload.navigation_root,
      panel_id: payload.panel_id,
      click_target_url: payload.click_target_url,
      visitor_id: payload.visitor_id,
      session_id: payload.session_id,
      tracking_version: payload.tracking_version,
      device_type: payload.device_type,
      panel_view_trigger: payload.panel_view_trigger,
      from_level: payload.from_level,
      to_level: payload.to_level,
      menu_level: payload.menu_level,
      is_view_all: payload.is_view_all,
      timestamp: payload.timestamp,
    };
    if (typeof console !== "undefined" && console.info) {
      console.info("[nav-tracking]", debugPayload);
    }
  }

  function sendNavigationEvent(payload) {
    var enriched = Object.assign(
      {
        page_title: getClickPageTitle(),
        device_type: getDeviceType(),
        timestamp: new Date().toISOString(),
        click_module_location: "header",
      },
      buildIdentityFields(),
      payload,
    );
    debugNavEvent(enriched);
    sendClickToGoogleSheet(enriched);
  }

  function isBlankLayoutClick(rawTarget, clickEl) {
    if (!rawTarget || rawTarget.nodeType !== 1) return true;
    if (
      (rawTarget.closest && rawTarget.closest(".accessory-item")) ||
      (clickEl && clickEl.closest && clickEl.closest(".accessory-item"))
    ) {
      return false;
    }
    var layoutTags = ["DIV", "SECTION", "MAIN", "PRODUCT-INFO"];
    if (layoutTags.indexOf(rawTarget.tagName) === -1) return false;
    if (
      rawTarget !== clickEl &&
      !isInteractiveClickElement(clickEl)
    ) {
      return !getClickText(rawTarget, clickEl);
    }
    var directText = "";
    for (var i = 0; i < rawTarget.childNodes.length; i++) {
      if (rawTarget.childNodes[i].nodeType === 3) {
        directText += rawTarget.childNodes[i].textContent;
      }
    }
    return !directText.trim();
  }

  // ── Desktop hover navigation (Navigation V2) ──────────────────────────────

  var desktopNavState = {
    menuOpenTimers: {},
    menuOpenSent: {},
    panelTimers: {},
    defaultPanelSent: {},
  };

  function isElementVisible(el) {
    if (!el || el.nodeType !== 1) return false;
    var style = window.getComputedStyle(el);
    if (style.display === "none" || style.visibility === "hidden") return false;
    if (el.offsetParent === null && style.position !== "fixed") return false;
    var rect = el.getBoundingClientRect();
    return rect.width > 0 && rect.height > 0;
  }

  var MEGA_MENU_MIN_EXPANDED_WIDTH = 200;

  function isMegaMenuContentUserVisible(contentEl) {
    if (!contentEl || !isElementVisible(contentEl)) return false;
    var style = window.getComputedStyle(contentEl);
    var opacity = parseFloat(style.opacity);
    if (!isNaN(opacity) && opacity < 0.05) return false;
    var rect = contentEl.getBoundingClientRect();
    if (rect.width < MEGA_MENU_MIN_EXPANDED_WIDTH) return false;
    if (rect.height < 40) return false;
    return true;
  }

  function isMegaMenuOpen(headerMenu) {
    if (!headerMenu) return false;
    var content = headerMenu.querySelector(".mega-menu__content");
    if (!isMegaMenuContentUserVisible(content)) return false;
    var details = headerMenu.querySelector("details.mega-menu");
    if (details && details.open) return true;
    var summary = details && details.querySelector("summary");
    if (summary && summary.getAttribute("aria-expanded") === "true") {
      return true;
    }
    return isPointerOverHeaderMenu(headerMenu);
  }

  function isPanelActiveVisible(panelId) {
    var panel = document.getElementById(panelId);
    return !!(
      panel &&
      panel.classList.contains("is-active") &&
      isElementVisible(panel)
    );
  }

  function isPointerOverHeaderMenu(headerMenu) {
    return !!(headerMenu && headerMenu.matches && headerMenu.matches(":hover"));
  }

  function isPointerOverL2OrPanel(l2Item, panelId) {
    if (l2Item && l2Item.matches && l2Item.matches(":hover")) return true;
    var panel = document.getElementById(panelId);
    return !!(panel && panel.matches && panel.matches(":hover"));
  }

  function resetDesktopMenuState(root) {
    clearTimeout(desktopNavState.menuOpenTimers[root]);
    delete desktopNavState.menuOpenTimers[root];
    delete desktopNavState.menuOpenSent[root];
    delete desktopNavState.defaultPanelSent[root];
  }

  function resetDesktopPanelState(panelId) {
    clearTimeout(desktopNavState.panelTimers[panelId]);
    delete desktopNavState.panelTimers[panelId];
  }

  function sendDesktopMenuOpen(navigationRoot) {
    sendNavigationEvent({
      event_type: "navigation_menu_open",
      navigation_root: navigationRoot,
      click_element_text: navigationRoot,
      click_element_selector: "nav-track:navigation_menu_open:" + navigationRoot,
      click_element_dom_tree: buildSyntheticNavDomTree(
        "navigation_menu_open",
        navigationRoot,
        null,
      ),
      click_raw_target: "navigation",
    });
  }

  function sendDesktopPanelView(navigationRoot, panelId, trigger) {
    sendNavigationEvent({
      event_type: "navigation_panel_view",
      navigation_root: navigationRoot,
      panel_id: panelId,
      click_element_text: panelId,
      click_element_selector: "nav-track:navigation_panel_view:" + panelId,
      click_element_dom_tree: buildSyntheticNavDomTree(
        "navigation_panel_view",
        navigationRoot,
        panelId,
      ),
      click_raw_target: "navigation",
      panel_view_trigger: trigger,
    });
  }

  function sendDefaultPanelView(headerMenu, navigationRoot) {
    if (desktopNavState.defaultPanelSent[navigationRoot]) return;
    var activeL2 = headerMenu.querySelector(
      "li.mega-menu__level2-item.is-active[data-target]",
    );
    if (!activeL2 || !activeL2.dataset || !activeL2.dataset.target) return;
    var panelId = activeL2.dataset.target;
    if (!isPanelActiveVisible(panelId)) return;
    desktopNavState.defaultPanelSent[navigationRoot] = true;
    sendDesktopPanelView(navigationRoot, panelId, "default");
    scanDesktopMenuImpressions(headerMenu, navigationRoot);
  }

  function bindDesktopL2Hover(headerMenu, navigationRoot, l2Item) {
    var panelId = l2Item.dataset && l2Item.dataset.target;
    if (!panelId) return;

    l2Item.addEventListener("mouseenter", function () {
      clearTimeout(desktopNavState.panelTimers[panelId]);
      desktopNavState.panelTimers[panelId] = setTimeout(function () {
        if (!isPointerOverL2OrPanel(l2Item, panelId)) return;
        if (!isMegaMenuOpen(headerMenu)) return;
        if (!isPanelActiveVisible(panelId)) return;
        sendDesktopPanelView(navigationRoot, panelId, "hover");
        scanDesktopMenuImpressions(headerMenu, navigationRoot);
      }, NAV_HOVER_MS);
    });

    l2Item.addEventListener("mouseleave", function () {
      clearTimeout(desktopNavState.panelTimers[panelId]);
    });
  }

  function initDesktopNavigationTracking() {
    if (!ENABLE_NAVIGATION_EXPOSURE_EVENTS) return;
    if (getDeviceType() !== "desktop") return;

    document
      .querySelectorAll("header-menu[data-header-nav-root]")
      .forEach(function (headerMenu) {
        if (headerMenu.dataset.navDesktopBound === "1") return;
        headerMenu.dataset.navDesktopBound = "1";
        var navigationRoot =
          headerMenu.dataset && headerMenu.dataset.headerNavRoot;
        if (!navigationRoot) return;

        headerMenu.querySelectorAll(
          "li.mega-menu__level2-item[data-target]",
        ).forEach(function (l2) {
          bindDesktopL2Hover(headerMenu, navigationRoot, l2);
        });

        headerMenu.addEventListener("mouseenter", function () {
          if (desktopNavState.menuOpenSent[navigationRoot]) return;
          clearTimeout(desktopNavState.menuOpenTimers[navigationRoot]);
          desktopNavState.menuOpenTimers[navigationRoot] = setTimeout(
            function () {
              if (desktopNavState.menuOpenSent[navigationRoot]) return;
              if (!isPointerOverHeaderMenu(headerMenu)) return;
              if (!isMegaMenuOpen(headerMenu)) return;
              desktopNavState.menuOpenSent[navigationRoot] = true;
              sendDesktopMenuOpen(navigationRoot);
              sendDefaultPanelView(headerMenu, navigationRoot);
            },
            NAV_HOVER_MS,
          );
        });

        headerMenu.addEventListener("mouseleave", function () {
          resetDesktopMenuState(navigationRoot);
          headerMenu
            .querySelectorAll("li.mega-menu__level2-item[data-target]")
            .forEach(function (l2) {
              if (l2.dataset && l2.dataset.target) {
                resetDesktopPanelState(l2.dataset.target);
              }
            });
        });
      });
  }

  // ── Click listener (preserved + Navigation V1 fields) ───────────────────

  document.addEventListener(
    "click",
    function (event) {
      var rawTarget = event.target;
      if (
        !rawTarget ||
        rawTarget === document.body ||
        rawTarget === document.documentElement
      ) {
        return;
      }
      if (!isTrackableClickTarget(rawTarget)) return;
      if (
        !PRODUCT_CLICK_TRACKING_ENABLED &&
        !PRODUCT_CLICK_GA4_ENABLED
      ) {
        return;
      }
      if (
        PRODUCT_CLICK_TRACKING_ENABLED &&
        !PRODUCT_CLICK_GA4_ENABLED &&
        PRODUCT_CLICK_MARKET_COUNTRY &&
        !isProductClickTargetMarket()
      ) {
        return;
      }

      if (getDeviceType() === "mobile" && PRODUCT_CLICK_TRACKING_ENABLED) {
        var backEl = isMobileNavigationBackButton(rawTarget);
        if (backEl && backEl.closest("#menu-drawer")) {
          sendMobileNavigationBack(backEl, rawTarget);
          return;
        }
      }

      var clickEl = resolveClickElement(rawTarget);
      if (!clickEl) return;

      var className = getElementClassName(clickEl);
      var clickText = getClickText(rawTarget, clickEl);
      if (isBlankLayoutClick(rawTarget, clickEl)) return;
      if (!shouldTrackClick(rawTarget, clickEl, clickText)) return;

      var elementId = clickEl.id || "";
      var clickModule = getClickModule(clickEl);
      var domTreePath = getDomTreePath(clickEl, { stopSelector: "html" });
      var clickElementDomTree = "HTMLElement: " + domTreePath;
      var clickElementSelector =
        clickEl.tagName.toLowerCase() +
        (elementId ? "#" + elementId : "") +
        (className
          ? "." +
            className
              .trim()
              .split(/\s+/)
              .join(".")
          : "");

      var navCtx = getNavigationContext(clickEl, rawTarget);
      var eventType = resolveNavigationClickEventType(
        clickEl,
        clickModule,
        navCtx.click_target_url,
      );

      var sheetPayload = Object.assign(
        {
          page_title: getClickPageTitle(),
          click_element_dom_tree: clickElementDomTree,
          click_module_location: clickModule,
          click_element_selector: clickElementSelector,
          click_element_text: clickText,
          click_raw_target: rawTarget.tagName.toLowerCase(),
          device_type: getDeviceType(),
          timestamp: new Date().toISOString(),
          event_type: eventType,
          navigation_root: navCtx.navigation_root,
          panel_id: navCtx.panel_id,
          click_target_url: navCtx.click_target_url,
        },
        buildIdentityFields(),
      );

      if (clickModule === "header") {
        var menuDuration = getMenuDurationMs();
        if (menuDuration !== null) {
          sheetPayload.menu_duration_ms = menuDuration;
        }
        var menuCtx = getHeaderMenuContext(clickEl, rawTarget);
        if (menuCtx) {
          sheetPayload.menu_level = menuCtx.menu_level;
          sheetPayload.is_view_all = menuCtx.is_view_all;
        }
      }

      debugNavEvent(sheetPayload);
      sendClickToGoogleSheet(sheetPayload);

      if (PRODUCT_CLICK_GA4_ENABLED && typeof gtag === "function") {
        gtag("event", "product_page_click", {
          send_to: "G-9JBRJ4F1KW",
          click_element_dom_tree: clickElementDomTree,
          click_element_selector: clickElementSelector,
          click_element_text: clickText,
          click_module_location: clickModule,
          click_raw_target: sheetPayload.click_raw_target,
        });
      }
    },
    { passive: true },
  );

  initProductClickDetectedCountry();
  initMenuDurationTracking();
  getVisitorId();
  getSessionId();

  function bootNavigationTracking() {
    initDesktopNavigationTracking();
    initMobileDrawerOpenTracking();
    initMobileImpressionTracking();
    initEcommerceFunnelTracking();
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", bootNavigationTracking);
  } else {
    bootNavigationTracking();
  }

  document.addEventListener("shopify:section:load", bootNavigationTracking);
})();
